import React, { useContext, useMemo, useState } from 'react';
import { ContextProps } from '../types/ContextProps';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { Manifest } from 'src/types/ViewModel/Stowage/Manifest/Manifest';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import { MasterPlanSyncData } from 'src/types/ViewModel/Stowage/MasterPlanSyncData';
import { RotationCallStatus } from 'src/types/ViewModel/Stowage/Rotation/RotationCallStatus';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { Violation } from 'src/types/ViewModel/Stowage/Violation/Violation';

type StowagePlanContextType = {
    activeStowagePlan: StowagePlan | null;
    setActiveStowagePlan: (plan: StowagePlan | null) => void;
    stowagePlans: StowagePlan[] | null;
    setStowagePlans: (plan: StowagePlan[] | null) => void;
    loadedMasterStowagePlan: StowagePlan;
    setLoadedMasterStowagePlan: (stowagePlan: StowagePlan) => void;
    manifest: Manifest | null;
    setManifest: (manifest: Manifest | null) => void;
    stowagePlanChanges: StowagePlanChange[];
    setStowagePlanChanges: (stowagePlanChanges: StowagePlanChange[]) => void;
    imosSyncChanges: StowagePlanChange[];
    setImosSyncChanges: (stowagePlanChanges: StowagePlanChange[]) => void;
    masterPlanSyncData: MasterPlanSyncData | null;
    setMasterPlanSyncData: (masterPlanSyncData: MasterPlanSyncData | null) => void;
    isStowagePlanUpdated: { value: boolean } | null;
    setIsStowagePlanUpdated: (value: { value: boolean | null }) => void;
    rotationCallStatuses: RotationCallStatus[] | null;
    setRotationCallStatuses: (rotationCallStatuses: RotationCallStatus[] | null) => void;
    unavailableTanks: Tank[] | null;
    setUnavailableTanks: (availableTanks: Tank[] | null) => void;
    unavailableTankViolations: Map<number, Violation[]> | null;
    setUnavailableTankViolations: (violations: Map<number, Violation[]> | null) => void;
    hasClosedOldStowagePlanModal: boolean;
    setHasClosedOldStowagePlanModal: (hasClosed: boolean) => void;
    activeOperationsFilters: string[] | null;
    setActiveOperationsFilters: (filter: string[] | null) => void;
};

export const StowagePlanContext = React.createContext<StowagePlanContextType>(undefined!);

export const StowagePlanProvider = ({ children }: ContextProps) => {
    const [activeStowagePlan, setActiveStowagePlan] = useState<StowagePlan | null>(null);
    const [stowagePlans, setStowagePlans] = useState<StowagePlan[] | null>(null);
    const [loadedMasterStowagePlan, setLoadedMasterStowagePlan] = useState<StowagePlan | null>(null);
    const [manifest, setManifest] = useState<Manifest | null>(null);
    const [stowagePlanChanges, setStowagePlanChanges] = useState<StowagePlanChange[]>([]);
    const [imosSyncChanges, setImosSyncChanges] = useState<StowagePlanChange[]>([]);
    const [masterPlanSyncData, setMasterPlanSyncData] = useState<MasterPlanSyncData | null>(null);
    const [isStowagePlanUpdated, setIsStowagePlanUpdated] = useState<{ value: boolean } | null>(null);
    const [rotationCallStatuses, setRotationCallStatuses] = useState<RotationCallStatus[] | null>(null);
    const [unavailableTanks, setUnavailableTanks] = useState<Tank[] | null>(null);
    const [unavailableTankViolations, setUnavailableTankViolations] = useState<Map<number, Violation[]> | null>(null);
    const [hasClosedOldStowagePlanModal, setHasClosedOldStowagePlanModal] = useState<boolean>(false);
    const [activeOperationsFilters, setActiveOperationsFilters] = useState<string[] | null>(['loading', 'unloading', 'transit', 'transfer']);

    const value = useMemo(
        () => ({
            activeStowagePlan,
            setActiveStowagePlan,
            stowagePlans,
            setStowagePlans,
            loadedMasterStowagePlan,
            setLoadedMasterStowagePlan,
            manifest,
            setManifest,
            stowagePlanChanges,
            setStowagePlanChanges,
            imosSyncChanges,
            setImosSyncChanges,
            masterPlanSyncData,
            setMasterPlanSyncData,
            isStowagePlanUpdated,
            setIsStowagePlanUpdated,
            rotationCallStatuses,
            setRotationCallStatuses,
            unavailableTanks,
            setUnavailableTanks,
            unavailableTankViolations,
            setUnavailableTankViolations,
            hasClosedOldStowagePlanModal,
            setHasClosedOldStowagePlanModal,
            activeOperationsFilters,
            setActiveOperationsFilters,
        }),
        [
            activeStowagePlan,
            stowagePlans,
            loadedMasterStowagePlan,
            manifest,
            stowagePlanChanges,
            imosSyncChanges,
            masterPlanSyncData,
            isStowagePlanUpdated,
            rotationCallStatuses,
            unavailableTanks,
            unavailableTankViolations,
            hasClosedOldStowagePlanModal,
            activeOperationsFilters,
        ],
    );

    return <StowagePlanContext.Provider value={value}>{children}</StowagePlanContext.Provider>;
};

export const useStowagePlanStore = () => useContext(StowagePlanContext);
